// src/modules/appointments/pages/AppointmentEditPage.jsx
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  getAppointmentsConfig,
  getAppointment,
  updateAppointment,
  addAppointmentResource,
  removeAppointmentResource,
  addAppointmentPayment,
} from "../api/appointmentsApi";
import { useAuth } from "../../../auth/AuthContext";
import AppointmentForm from "../components/AppointmentForm";
import AppointmentResourcesManager from "../components/AppointmentResourcesManager";
import AppointmentPayments from "../components/AppointmentPayments";

export default function AppointmentEditPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const [appt, setAppt] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    try {
      setLoading(true);
      setError("");
      const data = await getAppointment(id);
      setAppt(data);
    } catch (e) {
      setError(e.message || "No se pudo cargar la cita.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    getAppointmentsConfig()
      .then(setConfig)
      .catch((e) => console.warn("No se pudo cargar config de citas", e));
    // eslint-disable-next-line
  }, [id]);

  async function handleUpdate(payload) {
    try {
      setSaving(true);
      setError("");
      const resourceId = payload.resource_id;
      const data = await updateAppointment(id, {
        ...payload,
        resource_id: undefined,
        actor_user_id: user?.id ?? null,
      });
      let next = data;
      const currentResourceId = appt?.resources?.[0]?.resource_id;
      if (resourceId && Number(resourceId) !== Number(currentResourceId || 0)) {
        if (currentResourceId) {
          next = await removeAppointmentResource(id, currentResourceId);
        }
        next = await addAppointmentResource(id, {
          resource_id: resourceId,
          role: "principal",
        });
      } else if (!resourceId && currentResourceId) {
        next = await removeAppointmentResource(id, currentResourceId);
      }
      setAppt(next);
    } catch (e) {
      setError(e.message || "No se pudo actualizar la cita.");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddResource(r) {
    try {
      const data = await addAppointmentResource(id, r);
      setAppt(data);
    } catch (e) {
      setError(e.message || "No se pudo agregar el recurso.");
    }
  }

  async function handleRemoveResource(rid) {
    try {
      const data = await removeAppointmentResource(id, rid);
      setAppt(data);
    } catch (e) {
      setError(e.message || "No se pudo quitar el recurso.");
    }
  }

  async function handleAddPayment(p) {
    try {
      const data = await addAppointmentPayment(id, p);
      setAppt(data);
    } catch (e) {
      setError(e.message || "No se pudo registrar el pago.");
    }
  }

  const statusColor = useMemo(() => {
    const s = (appt?.status || "").toLowerCase();
    if (["confirmed", "confirmada"].includes(s)) return "#22c55e";
    if (["pending", "pendiente"].includes(s)) return "#eab308";
    if (["cancelled", "cancelada"].includes(s)) return "#ef4444";
    return "#6b7280";
  }, [appt]);

  if (loading) {
    return (
      <div className="p-4 text-xs text-[color:var(--muted)]">
        Cargando cita...
      </div>
    );
  }

  if (!appt) {
    return (
      <div className="p-4 text-xs text-red-600">
        No se encontró la cita.
      </div>
    );
  }

  return (
    <div className="appointments-module p-4 max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <div className="page-title">
            Cita {appt.code || `#${appt.id}`}
          </div>
          <p className="text-xs text-[color:var(--muted)]">
            Edita la información de la cita, administra recursos asignados y registra pagos.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            className="px-3 py-1 rounded-full text-[10px] font-semibold uppercase"
            style={{
              backgroundColor: statusColor + "20",
              color: statusColor,
            }}
          >
            {appt.status || "Sin estado"}
          </span>
          {appt?.start_at && (
            <div className="text-[10px] text-[color:var(--muted)]">
              {new Date(appt.start_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {/* Errores globales */}
      {error && (
        <div className="text-xs text-red-600 border border-red-300 rounded p-2">
          {error}
        </div>
      )}

      {/* Formulario principal */}
      <section className="card section">
        <h3 className="m-0 mb-2 text-sm font-semibold">Datos de la cita</h3>
        <AppointmentForm
          config={config}
          initialData={appt}
          onSubmit={handleUpdate}
          submitting={saving}
        />
      </section>

      {/* Recursos */}
      <section className="card section">
        <h3 className="m-0 mb-2 text-sm font-semibold">Recursos asignados</h3>
        <AppointmentResourcesManager
          appointment={appt}
          onAdd={handleAddResource}
          onRemove={handleRemoveResource}
        />
      </section>

      {/* Pagos */}
      <section className="card section">
        <h3 className="m-0 mb-2 text-sm font-semibold">Pagos</h3>
        <AppointmentPayments
          appointment={appt}
          onAdd={handleAddPayment}
        />
      </section>
    </div>
  );
}
